import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useModal } from '../../context/ModalContext';
import SideBar from './Conversations/SideBar/SideBar';
import ChatView from './Conversations/ChatView/ChatView';
import NewChatView from './Conversations/ChatView/NewChatView';
import { useDragResize } from '../../hooks/useDragResize';
import { useSortedConversations } from '../../hooks/useSorting';
import { useChannels } from '../../hooks/useChannels';
import { useLayout } from '../../context/LayoutContext';
import { useNavigation } from '../../context/NavigationContext';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import useLastViewedRoom from '../../hooks/useLastViewedRoom';
import useFileAttachment from '../../hooks/useFileAttachment';
import useAutoAcceptInvite from '../../hooks/useAutoAcceptInvite';
import { useFileDragDrop } from '../../hooks/useFileDragDrop';
import DragDropOverlay from '../DragDropOverlay/DragDropOverlay';
import './ChannelsPage.css';

const ChannelsPage = () => {
  const params = useParams();
  const { t } = useI18n();
  const { showError } = useModal();
  const { sideBarWidth, resizeSideBar, toggleMenu } = useLayout();
  
  // Track window width to auto-hide sidebar below 700px
  const isWideEnoughForSidebars = useWindowWidth(700);

  const { onMouseDown: onResizeBarMouseDown } = useDragResize({
    onResizeX: resizeSideBar,
    getCurrentX: () => sideBarWidth,
    enableX: true,
    enableY: false,
  });

  const { channelList, isLoading, createChannel, isChannelOwner } = useChannels();
  const { navigateToChannel, isCreatingNew, setNavigationGuard } = useNavigation();
  const sortedChannels = useSortedConversations(channelList);
  const [isEditing, setIsEditing] = useState(false);
  const { setLastViewed } = useLastViewedRoom('channels');
  
  // Track if we just created a channel to prevent auto-redirect
  const justCreatedChannelRef = useRef(null);

  const currentId = useMemo(() => {
    if (!params.id) return null;
    // Decode the ID from the URL (Matrix room IDs are URL-encoded)
    return params.id === 'new' ? null : decodeURIComponent(params.id);
  }, [params.id]);

  // File attachment management
  const {
    attachment,
    isUploading,
    fileInputRef,
    uploadProgress,
    handleStartUpload,
    handleStopUpload,
    handleCancelUpload,
    handleClearAttachment,
    handleFileSelect,
    handleTriggerFileSelect,
    setUploadProgress,
  } = useFileAttachment({
    clearOnRoomChange: true,
    currentRoomId: currentId,
  });

  // Validate if the ID from the URL is a real channel
  const channelExists = currentId && sortedChannels.some(channel => channel.id === currentId);

  // Determine the default channel to select if the URL is invalid
  const defaultId = useMemo(() => {
    // Always select the first (top) channel in the sorted list
    return sortedChannels[0]?.id || null;
  }, [sortedChannels]);

  // Auto-navigate to default channel if current channel is invalid and not creating new
  useEffect(() => {
    // Don't redirect if we just created this channel (it might not be in the list yet)
    if (currentId && justCreatedChannelRef.current === currentId) {
      return;
    }
    
    if (!isCreatingNew && !channelExists && defaultId) {
      navigateToChannel(defaultId);
    }
  }, [isCreatingNew, channelExists, defaultId, currentId, navigateToChannel]);

  // Auto-accept channel invites when navigating to invited channel
  useAutoAcceptInvite(currentId, sortedChannels, isCreatingNew, true);

  // Track the currently viewed channel
  useEffect(() => {
    if (currentId && channelExists && !isCreatingNew) {
      setLastViewed(currentId);
      // Clear the "just created" flag once the channel exists in the list
      if (justCreatedChannelRef.current === currentId) {
        justCreatedChannelRef.current = null;
      }
    }
  }, [currentId, channelExists, isCreatingNew, setLastViewed]);

  // Set up the navigation guard for creating or editing
  useEffect(() => {
    if (isCreatingNew) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.discardNewConversationErrorTitle'),
        message: t('ChatView.discardNewConversationErrorDesc'),
        confirmText: t('discard'),
        cancelText: t('cancel'),
      });
    } else if (isEditing) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.discardChangesErrorTitle'),
        message: t('ChatView.discardChangesErrorDesc'),
        onConfirm: () => setIsEditing(false), // Reset editing state if user confirms
        confirmText: t('discard'),
        cancelText: t('cancel'),
      });
    } else if (isUploading) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.cancelUploadErrorTitle'),
        message: t('ChatView.cancelUploadErrorDesc'),
        onConfirm: handleCancelUpload,
        confirmText: t('yes'),
        cancelText: t('no'),
      });
    } else {
      // Ensure the guard is inactive if not creating or editing
      setNavigationGuard({ isActive: false });
    }

    // Cleanup function to deactivate the guard when the component unmounts
    return () => setNavigationGuard({ isActive: false });
  }, [isCreatingNew, isEditing, isUploading, setNavigationGuard, handleCancelUpload, t]);

  const handleChannelSelect = useCallback((channelId) => {
    navigateToChannel(channelId);
  }, [navigateToChannel]);

  const handleCreateChannel = useCallback(() => {
    navigateToChannel('new');
  }, [navigateToChannel]);

  const handleCreateChannelSave = useCallback(async (channelData) => {
    const {
      title,
      selectedContacts,
      initialMessageText,
      isPublicRoom = false, // Default to private if not specified
    } = channelData;

    try {
      // For channels: name, topic (use initialMessage as topic), inviteUsers, visibility
      const newChannelId = await createChannel(
        title,
        initialMessageText || '', // Use initial message as channel topic/description
        selectedContacts.map(c => c.id),
        isPublicRoom, // Pass public/private setting
      );

      if (newChannelId) {
        // Mark this channel as just created to prevent auto-redirect
        justCreatedChannelRef.current = newChannelId;
        // Navigate to the new channel, bypassing the guard for this successful action
        navigateToChannel(newChannelId, { bypassGuard: true });
      }
    } catch (error) {
      showError(
        t('ChannelsPage.createChannelErrorTitle'),
        error.message || t('ChannelsPage.createChannelErrorMessage'),
      );
    }
  }, [createChannel, navigateToChannel, showError, t]);

  const handleCreateChannelCancel = useCallback(() => {
    navigateToChannel(null);
  }, [navigateToChannel]);

  const handleLeaveChannel = useCallback(() => {
    // After leaving a channel, navigate back to the channels page
    navigateToChannel(null);
  }, [navigateToChannel]);

  // Drag & Drop for file attachments
  const handleFilesDropped = useCallback((files) => {
    // Simulate file input change event
    const fakeEvent = {
      target: {
        files: files,
      },
    };
    handleFileSelect(fakeEvent);
  }, [handleFileSelect]);

  const { isDraggingOver, dragHandlers } = useFileDragDrop({
    currentRoomId: currentId,
    isCreatingNew,
    onFilesDropped: handleFilesDropped,
  });

  const renderSideBar = () => {
    // Hide sidebar when screen width < 700px
    if (!isWideEnoughForSidebars) return null;

    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: `${sideBarWidth}px`,
    };

    return (
      <div style={style}>
        <SideBar
          conversations={sortedChannels}
          isCreatingConversation={isCreatingNew}
          selectedConversationId={currentId}
          setCurrentConversationId={handleChannelSelect}
          onCreateConversationClick={handleCreateChannel}
          onMenuToggle={toggleMenu}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const renderResizeBar = () => {
    // Hide resize bar when sidebar is hidden
    if (!isWideEnoughForSidebars) return null;

    const style = {
      position: 'absolute',
      inset: `0px 0px 0px ${sideBarWidth}px`,
      width: '4px',
      zIndex: 1000,
      cursor: 'e-resize',
    };

    return <div style={style} onMouseDown={onResizeBarMouseDown}></div>;
  };

  const renderMainContent = () => {
    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      // Full width when sidebar is hidden by responsive constraint
      width: isWideEnoughForSidebars ? `calc(100% - ${sideBarWidth}px)` : '100%',
    };

    if (isCreatingNew) {
      return (
        <div style={style}>
          <NewChatView 
            onSave={handleCreateChannelSave} 
            onCancel={handleCreateChannelCancel}
            isChannel={true}
          />
        </div>
      );
    }

    if (!channelExists) {
      return <div style={style} />;
    }

    return (
      <div style={style}>
        <ChatView
          roomId={currentId}
          isEditing={isEditing}
          startEdit={() => setIsEditing(true)}
          closeEdit={() => setIsEditing(false)}
          startUpload={handleStartUpload}
          stopUpload={handleStopUpload}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          setUploadProgress={setUploadProgress}
          onCancelUpload={handleCancelUpload}
          attachment={attachment}
          onTriggerFileSelect={handleTriggerFileSelect}
          onClearAttachment={handleClearAttachment}
          canEdit={isChannelOwner(currentId)}
          onLeaveRoom={handleLeaveChannel}
        />
      </div>
    );
  };

  return (
    <div 
      className="channels-page"
      {...dragHandlers}
    >
      { renderSideBar() }
      { renderMainContent() }
      { renderResizeBar() }
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
      />
      {/* Drag & Drop Overlay */}
      <DragDropOverlay 
        text={t('ConversationsPage.dropFileHere')}
        isVisible={isDraggingOver}
      />
    </div>
  );
};

export default ChannelsPage;
